import {
  hooks,
  DropdownMenus,
  DropdownMenu,
  DropdownMenuItem,
  Flex,
  Icon,
  Placement,
  PlacementArea,
  PlacementManager,
  PlacementReference,
  ProgressSpinner,
  Text,
  Visibility,
  VisibilityContainer,
} from '@bitrise/bitkit';
import { TypeAttribute } from '@trace/types';
import classnames from 'classnames';
import React, { useContext, useState, FunctionComponent } from 'react';
import { ATTRIBUTE_NAME_MAP } from '../../constants/Attributes';
import dataAtIds from '../../dataAtIds.json';
import useFilters from './useFilters';
import { trackEvent } from '../../utils/tracking';
import { AttributesContext } from '../Attributes/Attributes';
import './FiltersSelector.css';

const { useMediaQuery } = hooks;

interface Props {
  onChange?: () => void;
}

const FiltersDropdown: FunctionComponent<Props> = (props) => {
  const { onChange } = props;
  const { isFilterSelected, toggleFilter } = useFilters();
  const { attributes, attributesValues, isEmpty, isFetched, isFetching } = useContext(
    AttributesContext,
  );
  const [[visible, group], setVisibility] = useState<[boolean, null | TypeAttribute]>([
    false,
    null,
  ]);
  const match = useMediaQuery(['564px']);
  const groupAttributes = group && attributesValues[group];

  const classes = classnames('FiltersDropdown', {
    'FiltersDropdown--active': visible,
  });

  const handleSelectFilter = (value: string) => {
    if (group) {
      trackEvent(isFilterSelected({ type: group, value }) ? 'Filters.Removed' : 'Filters.Added', {
        attribute: group,
        value,
      });

      toggleFilter({ type: group, value });

      if (typeof onChange === 'function') {
        onChange();
      }
    }
  };

  return (
    <PlacementManager>
      <PlacementReference>
        {({ ref }) => (
          <Flex
            backgroundColor="grape-1"
            borderRadius="x2"
            className={classes}
            clickable
            data-trace-at-id={dataAtIds.AttributeFiltersButton}
            direction="horizontal"
            gap="x3"
            innerRef={ref}
            onClick={() => setVisibility([true, null])}
            textColor="grape-3"
          >
            <Flex>
              <Text weight="bold">Session Filters</Text>
            </Flex>

            <Flex>
              <Icon name="Filter" />
            </Flex>
          </Flex>
        )}
      </PlacementReference>

      <Placement
        disableReposition
        onClose={() => setVisibility([false, null])}
        placement="bottom-start"
        visible={visible}
      >
        {() => (
          <>
            {isFetched && !isEmpty && (
              <DropdownMenus onMouseLeave={() => setVisibility([false, null])}>
                {(match('564px') || !group) && (
                  <DropdownMenu maxHeight="18rem" width="15rem">
                    {attributes.map((attribute) => (
                      <DropdownMenuItem
                        Component={VisibilityContainer}
                        backgroundColor={attribute === group ? 'grape-1' : undefined}
                        data-trace-at-id={dataAtIds.AttributeFiltersMenuGroup}
                        key={attribute}
                        onClick={() => setVisibility([true, attribute])}
                      >
                        <Flex
                          alignChildrenVertical="middle"
                          direction="horizontal"
                          gap="x2"
                          grow
                          textColor={attribute === group ? 'grape-3' : undefined}
                        >
                          <Flex grow initial="none">
                            <Text ellipsis>{ATTRIBUTE_NAME_MAP[attribute]}</Text>
                          </Flex>

                          <Flex>
                            <Icon name="ChevronRight" />
                          </Flex>
                        </Flex>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenu>
                )}

                {group && groupAttributes && (
                  <DropdownMenu key={group} maxHeight="18rem" width="18rem">
                    {groupAttributes.map((value) => (
                      <DropdownMenuItem
                        Component={VisibilityContainer}
                        data-trace-at-id={dataAtIds.AttributeFiltersMenuItem}
                        key={value}
                        onClick={() => handleSelectFilter(value)}
                        selected={isFilterSelected({ type: group, value })}
                        textColor={isFilterSelected({ type: group, value }) ? 'grape-3' : undefined}
                        title={value}
                      >
                        <Flex alignChildrenVertical="middle" direction="horizontal" gap="x8" grow>
                          <Flex grow initial="none">
                            <Text ellipsis>{value}</Text>
                          </Flex>

                          <Visibility Component={Flex}>
                            <Icon
                              name={
                                isFilterSelected({ type: group, value }) ? 'MinusRemove' : 'PlusAdd'
                              }
                            />
                          </Visibility>
                        </Flex>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenu>
                )}
              </DropdownMenus>
            )}

            {isFetching && (
              <PlacementArea padding="x6">
                <ProgressSpinner />
              </PlacementArea>
            )}

            {isEmpty && (
              <PlacementArea padding="x6">There are no attributes to group by.</PlacementArea>
            )}
          </>
        )}
      </Placement>
    </PlacementManager>
  );
};

export default FiltersDropdown;
